import { Relation } from '@cotera/era';
import { CoteraDuckDB, DuckDBPool } from '../db';
import { DuckDBQueryResult } from './duckdb-query-result';
export { CoteraDuckDB } from '../db';
export { DuckDBQueryResult } from './duckdb-query-result';
export {
  duckDbIdentifier,
  duckDbStringLiteral,
  duckDbValueLiteral,
} from './sql';
export {
  fromArrowRow,
  fromArrowValue,
  parseRowCount,
  readNumber,
  readString,
  type DuckDbCellValue,
} from './arrow';
export {
  ParquetBackedTable,
  type ParquetBackedTableDb,
} from './parquet-backed-table';
export {
  DuckDbPartitionedTable,
  DuckDbTable,
  type DuckDbTableSource,
  type DuckDbTableWriter,
  type DuckDbWriteTarget,
} from './table';

/**
 * Lazily loads the DuckDB WASM module and initializes the database.
 * Both the JS module and WASM binary are loaded on-demand.
 */
export const duckDbSetup = async (): Promise<CoteraDuckDB> => {
  // Dynamic import - loads @duckdb/duckdb-wasm only when this function is called
  const duckdb = await import('@duckdb/duckdb-wasm');

  const JSDELIVR_BUNDLES = duckdb.getJsDelivrBundles();
  const bundle = await duckdb.selectBundle(JSDELIVR_BUNDLES);
  const crossOriginWorkerUrl = getWorkerURL(bundle.mainWorker!);

  const worker = new Worker(crossOriginWorkerUrl);
  const logger = new duckdb.ConsoleLogger(duckdb.LogLevel.WARNING);
  const db = new duckdb.AsyncDuckDB(logger, worker);

  await db.instantiate(bundle.mainModule, bundle.pthreadWorker);
  return new CoteraDuckDB(
    db,
    new DuckDBPool(db, {
      min: 1,
      // The cap on how many queries run against this instance at once, and so
      // on how much transient query memory is live at the peak. All connections
      // share one database inside one wasm worker whose linear memory never
      // returns to the OS, so that peak becomes the tab's permanent floor —
      // six concurrent scans set a higher floor than the preview's page +
      // count + per-column-stats fan-out actually needs. Three keeps that
      // fan-out overlapping while holding the high-water mark down.
      max: 3,
      testOnBorrow: true,
    })
  );
};

/**
 * Creates a lazy promise that only executes the DuckDB setup when first awaited.
 * This allows deferring the heavy DuckDB WASM loading until it's actually needed
 * (e.g., when visiting datasets/reports pages).
 *
 * The implementation uses a Promise wrapper that defers the actual duckDbSetup()
 * call until the promise is first awaited. Once initialized, the same promise
 * is reused for all subsequent calls.
 */
export const createLazyDuckDb = (): Promise<CoteraDuckDB> => {
  let cachedPromise: Promise<CoteraDuckDB> | null = null;
  let resolveOuter: ((value: CoteraDuckDB) => void) | null = null;
  let rejectOuter: ((reason: unknown) => void) | null = null;

  const outerPromise = new Promise<CoteraDuckDB>((resolve, reject) => {
    resolveOuter = resolve;
    rejectOuter = reject;
  });

  const startInit = () => {
    if (cachedPromise) {
      return;
    }
    cachedPromise = duckDbSetup();
    cachedPromise
      .then((db) => {
        // Set the global window reference when db is ready
        if (typeof window !== 'undefined') {
          window.COTERA_DUCKDB = db;
        }
        resolveOuter!(db);
      })
      .catch((err) => {
        rejectOuter!(err);
      });
  };

  // Wrap the promise to intercept access and trigger initialization
  const lazyPromise = {
    then<TResult1 = CoteraDuckDB, TResult2 = never>(
      onfulfilled?:
        | ((value: CoteraDuckDB) => TResult1 | PromiseLike<TResult1>)
        | null,
      onrejected?:
        | ((reason: unknown) => TResult2 | PromiseLike<TResult2>)
        | null
    ): Promise<TResult1 | TResult2> {
      startInit();
      return outerPromise.then(onfulfilled, onrejected);
    },
    catch<TResult = never>(
      onrejected?: ((reason: unknown) => TResult | PromiseLike<TResult>) | null
    ): Promise<CoteraDuckDB | TResult> {
      startInit();
      return outerPromise.catch(onrejected);
    },
    finally(onfinally?: (() => void) | null): Promise<CoteraDuckDB> {
      startInit();
      return outerPromise.finally(onfinally);
    },
    [Symbol.toStringTag]: 'LazyDuckDbPromise',
  };

  return lazyPromise as Promise<CoteraDuckDB>;
};

export type QueryData = {
  data: DuckDBQueryResult;
  artifactRel: Relation;
  fromArtifactId: string | null;
};

// Returns a blob:// URL which points
// to a javascript file which will call
// importScripts with the given URL
function getWorkerURL(url: string) {
  const content = `importScripts( "${url}" );`;
  return URL.createObjectURL(new Blob([content], { type: 'text/javascript' }));
}
